import { BarChart3, ListChecks } from 'lucide-react';
import { useState } from 'react';
import { FolderTabs } from '../../../../components/FolderTabs';
import { ProfileNav } from '../../../../components/profile/ProfileNav';
import { SurveyQuestionView } from './SurveyQuestionView';
import { SurveyIndividualView } from './SurveyIndividualView';
import { SurveySummaryView } from './SurveySummaryView';

export type ResponsesKey = 'summary' | 'individual' | 'question';

/** The strip's own two tabs. Summary and Question are both inside Statistics. */
type ResponsesTab = 'statistics' | 'assessment';

/**
 * The survey profile's Responses tab — a second strip of folder tabs, the Configuration tab's
 * frame exactly:
 *
 * - **Statistics** (product call, 13 Oct 2026) — a SIDE NAV on the left choosing between Summary
 *   (a chart per question) and Question (every respondent's answer to one question). The shared
 *   `<ProfileNav>`, so the rail reads as every other one in the product.
 * - **Assessment** — one respondent's answers, and their assessment.
 *
 * `ResponsesKey` still names the three VIEWS, which is what the page lands by; the tab is derived.
 */
export function SurveyResponsesPanel({
  surveyId,
  surveyActive,
  initialView = 'summary',
  onChanged,
}: {
  /** The sub-tab it opens on — Assessment (key `individual`) when the page landed here to assess. */
  initialView?: ResponsesKey;
  surveyId: string;
  surveyActive: boolean;
  /** After an assessment or a delete — the page re-reads the counts that gate its tabs. */
  onChanged?: () => void;
}) {
  const [view, setView] = useState<ResponsesKey>(initialView);
  // Which side-nav entry Statistics was last on — kept while the reader is over on Assessment.
  const [statistic, setStatistic] = useState<'summary' | 'question'>(initialView === 'question' ? 'question' : 'summary');

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
      <div className="flex min-h-0 flex-1 flex-col">
        <FolderTabs<ResponsesTab>
          id="survey-responses"
          ariaLabel="Responses"
          paddingX="px-3"
          activeKey={view === 'individual' ? 'assessment' : 'statistics'}
          // Back into Statistics lands on the side-nav entry last open there.
          onChange={(next) => setView(next === 'assessment' ? 'individual' : statistic)}
          tabs={[
            { key: 'statistics', label: 'Statistics' },
            { key: 'assessment', label: 'Assessment' },
          ]}
        />

        {/* All four borders, as Configuration's — the active tab's white edge breaks the top. */}
        <div className="min-h-0 flex-1 rounded-b-panel border border-slate-300 bg-white">
          {view === 'individual' ? (
            <SurveyIndividualView surveyId={surveyId} surveyActive={surveyActive} onChanged={onChanged} />
          ) : (
            <div className="flex h-full min-h-0 min-w-0">
              <aside className="modern-scrollbar w-56 shrink-0 overflow-y-auto overflow-x-hidden border-r border-slate-200">
                <ProfileNav
                  id="survey-statistics-nav"
                  entries={[
                    { key: 'summary', label: 'Summary', icon: <BarChart3 className="h-4 w-4" aria-hidden="true" /> },
                    { key: 'question', label: 'Question', icon: <ListChecks className="h-4 w-4" aria-hidden="true" /> },
                  ]}
                  activeKey={view}
                  onSelect={(key) => {
                    const next = key === 'question' ? 'question' : 'summary';

                    setStatistic(next);
                    setView(next);
                  }}
                />
              </aside>

              <div className="min-h-0 min-w-0 flex-1">
                {view === 'summary' ? <SurveySummaryView surveyId={surveyId} /> : <SurveyQuestionView surveyId={surveyId} />}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
